import type { QueryClient } from "@tanstack/react-query";

import { Document, UploadedDocument } from "@/shared/domains";
import { parseResponseData, privateAPI, publicAPI } from "@/shared/lib";
import type { ServiceResponse } from "@/shared/lib/axios";
import type {
  Folder,
  UploadedFile,
  UploadFilesValues,
} from "@/shared/models/legacy-types";

const DOMAIN = "documents";

export const getDocumentQuery = (
  documentId?: string | null,
  vendor?: string | null,
) => ({
  enabled: !!documentId,
  queryKey: [DOMAIN, "getDocumentUrl", documentId, vendor],
  queryFn: async () => {
    const response = await privateAPI.get(`/documents/${documentId}`, {
      params: vendor ? { vendor } : undefined,
    });

    return parseResponseData(response, UploadedDocument);
  },
});

export const updateDocument = {
  mutation: async ({
    id,
    name,
    vendor,
  }: Omit<UploadedFile, "createdAt"> & { vendor?: string | null }) => {
    const { data } = await privateAPI.put<ServiceResponse<null>>(
      `/documents/${id}`,
      { name },
      { params: vendor ? { vendor } : undefined },
    );

    return data.data;
  },
  invalidates: (queryClient: QueryClient) => {
    void queryClient.invalidateQueries({ queryKey: [DOMAIN] });
  },
};

export const deleteDocument = {
  mutation: async ({
    id,
    vendor,
  }: {
    id: UploadedFile["id"];
    vendor?: string | null;
  }) => {
    const { data } = await privateAPI.delete<ServiceResponse<null>>(
      `/documents/${id}`,
      { params: vendor ? { vendor } : undefined },
    );

    return data.data;
  },
  invalidates: (queryClient: QueryClient) => {
    void queryClient.invalidateQueries({ queryKey: [DOMAIN] });
  },
};

export const uploadTempFile = {
  mutation: async (file: File) => {
    const { data } = await publicAPI.post<ServiceResponse<{ fileId: string }>>(
      "/temp-files",
      { file },
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    return data.data;
  },
};

const getFieldsWithoutFiles = (fields: UploadFilesValues["files"]) =>
  fields.map((field) => ({ id: field.id, name: field.name }));

export const submitUploadedDocuments = {
  mutation: async (params: UploadFilesValues) => {
    const { data } = await privateAPI.post<ServiceResponse<null>>(
      "/temp-files/upload-files",
      {
        uploadedFiles: [
          {
            type: params.type,
            files: getFieldsWithoutFiles(params.files),
          },
        ],
      },
    );

    return data.data;
  },
  invalidates: (queryClient: QueryClient) => {
    void queryClient.invalidateQueries({ queryKey: [DOMAIN] });
  },
};

export const getDocumentsQuery = (type: Folder) => ({
  queryKey: [DOMAIN, "getDocumentsQuery", type],
  queryFn: async () => {
    const response = await privateAPI.get(`/documents`, { params: { type } });

    return parseResponseData(response, Document.array());
  },
});
